import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  LongevityAssessmentDetail,
  LongevityAssessmentUpdate,
  LongevityManagerCase,
  LongevityTestRow,
  ManagerCaseStatus,
} from './longevity-alerts.model';
import {
  normalizeAssessmentDetail,
  normalizeManagerCasePage,
  normalizeTestPage,
} from './longevity-alerts.mapper';

const CASE_STATUSES: ManagerCaseStatus[] = ['OPEN', 'TAKEN_IN_CHARGE', 'CLOSED'];

/**
 * Endpoint dello staff del Longevity Index (ruoli LONGEVITY_MANAGER, FAMILYTUTOR, ADMIN), dal documento
 * "Longevity Index – API per il frontend" del 16 ottobre 2026: lista dei test con i loro casi Manager
 * (`GET /manager/cases`), dettaglio di un test (`GET /assessments/{id}`) e l'unico modo di modificarlo
 * (`PATCH /assessments/{id}`: stato del caso, commento, piano, operatore). Lo usano sia la coda della
 * pagina Avvisi sia l'elenco dei test di una richiesta.
 */
@Injectable({
  providedIn: 'root',
})
export class LongevityAlertsService {
  private readonly baseUrl = `${environment.baseURL}/longevity-index`;

  constructor(private http: HttpClient) {}

  /**
   * Casi Manager delle richieste assegnate all'operatore (uid). Una pagina abbastanza grande da non
   * dover paginare nei volumi reali (come loadAlerts). La lista del backend contiene tutti i test, anche
   * senza caso: senza filtro di stato si chiedono i tre stati e si uniscono, così i test senza caso non
   * riempiono la pagina al posto dei casi.
   */
  findCases(
    operatorUid: string,
    operatorName: string,
    status: ManagerCaseStatus | '' = ''
  ): Observable<LongevityManagerCase[]> {
    const statuses = status ? [status] : CASE_STATUSES;
    return forkJoin(
      statuses.map((s) =>
        this.http
          .get<unknown>(`${this.baseUrl}/manager/cases`, {
            params: new HttpParams()
              .set('operator', operatorUid)
              .set('status', s)
              .set('page', 0)
              .set('size', 100),
          })
          .pipe(map((raw) => normalizeManagerCasePage(raw, operatorName)))
      )
    ).pipe(
      map((pages) =>
        pages.reduce<LongevityManagerCase[]>((all, rows) => all.concat(rows), [])
      )
    );
  }

  /** Tutti i test di una richiesta (con o senza caso), dal più recente. Non apre i test: nessun accesso nel log. */
  findTestsOfRequest(requestId: number): Observable<LongevityTestRow[]> {
    return this.http
      .get<unknown>(`${this.baseUrl}/manager/cases`, {
        params: new HttpParams().set('requestId', requestId).set('page', 0).set('size', 100),
      })
      .pipe(map(normalizeTestPage));
  }

  /**
   * Un test per intero. Se il test ha un caso l'apertura finisce nel log degli accessi (chi e quando):
   * va chiamato solo quando l'operatore lo apre davvero, mai per precaricare.
   */
  getAssessmentDetail(id: string): Observable<LongevityAssessmentDetail> {
    return this.http
      .get<unknown>(`${this.baseUrl}/assessments/${id}`)
      .pipe(map(normalizeAssessmentDetail));
  }

  /**
   * Modifica un test: o vanno a buon fine tutti i campi inviati o non cambia niente. Risponde con il
   * dettaglio già aggiornato. 409 INVALID_STATE se il caso è già in mano a un altro (o chiuso): il
   * chiamante deve ricaricare il dettaglio.
   */
  updateAssessment(id: string, update: LongevityAssessmentUpdate): Observable<LongevityAssessmentDetail> {
    return this.http
      .patch<unknown>(`${this.baseUrl}/assessments/${id}`, update)
      .pipe(map(normalizeAssessmentDetail));
  }

  takeInCharge(id: string): Observable<LongevityAssessmentDetail> {
    return this.updateAssessment(id, { status: 'TAKEN_IN_CHARGE' });
  }

  close(id: string): Observable<LongevityAssessmentDetail> {
    return this.updateAssessment(id, { status: 'CLOSED' });
  }
}
